import type { LlmEntry } from "../config";
import type { StructuredResult } from "../types";
import { copyToClipboard } from "../utils/export";
import { useMemo, useState } from "react";
import { LlmPill } from "./LlmPill";
import { friendlyLabel } from "../utils/llmLabels";

type Candidate = {
  rank?: number;
  model?: string;
  pseudonym?: string;
  average_score?: number;
  text?: string;
  peer_reviews?: Array<{
    reviewer_model?: string;
    score?: number;
    justification?: string;
  }>;
};

/**
 * Compose produces a ranked list of candidate texts — each model drafts a
 * response, then every model peer-reviews the others with a 0-1 score + a
 * justification. The MAGI-Service puts the aggregate under
 * `aggregate.ranked_candidates` and leaves `rapporteur: null`. Without this
 * component, the Compose UI ended up empty below the verdict strip.
 */
export function ComposeRanking({
  result,
  availableLlms,
}: {
  result: StructuredResult;
  availableLlms: LlmEntry[];
}) {
  const round = result.rounds?.at(-1);
  const agg = round?.aggregate ?? {};
  const ranked = (agg.ranked_candidates ?? []) as Candidate[];
  const meta = useMemo(
    () => new Map(availableLlms.map((l) => [l.id, l])),
    [availableLlms],
  );
  if (ranked.length === 0) return null;

  return (
    <section
      aria-label="Compose ranking"
      data-testid="compose-ranking"
      style={{ display: "flex", flexDirection: "column", gap: 12 }}
    >
      <h3 className="section-label" style={{ margin: 0 }}>
        Ranked candidates
      </h3>
      {ranked.map((c, i) => (
        <CandidateCard
          key={`${c.model ?? c.pseudonym ?? i}-${i}`}
          candidate={c}
          isTop={i === 0}
          fallbackRank={i + 1}
          meta={meta}
        />
      ))}
    </section>
  );
}

function CandidateCard({
  candidate,
  isTop,
  fallbackRank,
  meta,
}: {
  candidate: Candidate;
  isTop: boolean;
  fallbackRank: number;
  meta: Map<string, LlmEntry>;
}) {
  const [copied, setCopied] = useState(false);
  const rank = candidate.rank ?? fallbackRank;
  const score =
    typeof candidate.average_score === "number"
      ? candidate.average_score
      : null;
  const text = candidate.text ?? "";
  const reviews = candidate.peer_reviews ?? [];

  return (
    <article
      className={`compose-card${isTop ? " rank-1" : ""}`}
      data-testid={`compose-card-${rank}`}
      style={{
        border: "1px solid var(--border)",
        borderLeft: `3px solid ${isTop ? "var(--accent)" : "var(--border-strong)"}`,
        background: isTop ? "var(--accent-soft)" : "var(--bg-panel)",
        borderRadius: "var(--radius-md)",
        padding: "16px 18px",
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      <header
        className="row"
        style={{ justifyContent: "space-between", alignItems: "flex-start" }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: isTop ? "var(--accent)" : "var(--text-dim)",
            }}
          >
            Rank {rank}
            {isTop && " · top"}
          </div>
          <div style={{ marginTop: 2 }}>
            {candidate.model ? (
              <LlmPill
                id={candidate.model}
                label={friendlyLabel(candidate.model, meta)}
              />
            ) : (
              <span className="muted">—</span>
            )}
          </div>
        </div>
        <ScoreBadge score={score} />
      </header>

      {text && (
        <div
          style={{
            fontSize: 15,
            lineHeight: 1.65,
            whiteSpace: "pre-wrap",
            overflowWrap: "anywhere",
            wordBreak: "break-word",
            background: "var(--bg-panel-2)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
            padding: "12px 14px",
          }}
        >
          {text}
        </div>
      )}

      <div className="row" style={{ gap: 8 }}>
        {text && (
          <button
            className="btn ghost"
            onClick={async () => {
              if (await copyToClipboard(text)) {
                setCopied(true);
                setTimeout(() => setCopied(false), 1200);
              }
            }}
          >
            {copied ? "COPIED ✓" : "⎘ COPY TEXT"}
          </button>
        )}
        {reviews.length > 0 && (
          <details
            style={{
              flex: 1,
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-sm)",
              background: "var(--bg-panel-2)",
            }}
          >
            <summary
              style={{
                cursor: "pointer",
                padding: "8px 12px",
                fontSize: 13,
                fontWeight: 600,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "var(--text-dim)",
                userSelect: "none",
                listStyle: "none",
              }}
            >
              {reviews.length} peer review
              {reviews.length === 1 ? "" : "s"}
            </summary>
            <div style={{ padding: "6px 12px 12px", display: "flex", flexDirection: "column", gap: 10 }}>
              {reviews.map((r, i) => (
                <div
                  key={i}
                  style={{
                    fontSize: 14,
                    lineHeight: 1.55,
                    borderTop: i === 0 ? "none" : "1px solid var(--border)",
                    paddingTop: i === 0 ? 4 : 8,
                  }}
                >
                  <div
                    className="row"
                    style={{ justifyContent: "space-between", alignItems: "baseline" }}
                  >
                    {r.reviewer_model ? (
                      <LlmPill
                        id={r.reviewer_model}
                        label={friendlyLabel(r.reviewer_model, meta)}
                      />
                    ) : (
                      <span className="muted">—</span>
                    )}
                    <span style={{ color: "var(--accent)", fontWeight: 600 }}>
                      {typeof r.score === "number" ? r.score.toFixed(2) : "—"}
                    </span>
                  </div>
                  {r.justification && (
                    <div
                      style={{
                        marginTop: 4,
                        color: "var(--text)",
                        whiteSpace: "pre-wrap",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {r.justification}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </details>
        )}
      </div>
    </article>
  );
}

function ScoreBadge({ score }: { score: number | null }) {
  if (score === null) {
    return (
      <div
        style={{
          width: 64,
          textAlign: "right",
          color: "var(--text-dim)",
          fontSize: 14,
        }}
      >
        —
      </div>
    );
  }
  const pct = Math.max(0, Math.min(1, score));
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        minWidth: 80,
      }}
      title={`Average peer score ${score.toFixed(2)}`}
    >
      <div
        style={{
          fontSize: 20,
          fontWeight: 700,
          color: "var(--accent)",
          lineHeight: 1,
        }}
      >
        {score.toFixed(2)}
      </div>
      <div
        style={{
          fontSize: 10,
          letterSpacing: "0.14em",
          color: "var(--text-dim)",
          marginTop: 2,
        }}
      >
        AVG SCORE
      </div>
      <div
        style={{
          marginTop: 6,
          width: 70,
          height: 4,
          background: "var(--bg-panel-2)",
          border: "1px solid var(--border)",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${pct * 100}%`,
            height: "100%",
            background: "var(--accent)",
            transition: "width 0.6s ease",
          }}
        />
      </div>
    </div>
  );
}
